import React, { useEffect, useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Card,
  CardContent,
  Snackbar,
  useTheme,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StoreIcon from "@mui/icons-material/Store";
import MapIcon from "@mui/icons-material/Map";
import shopStore from "../../stores/ShopStore";
import CustomDataGridPro from "../CustomDataGridPro";

const ShopComponent = observer(() => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentShopId, setCurrentShopId] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [shopData, setShopData] = useState({
    name: "",
    latitude: "",
    longitude: "",
    street: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    shopStore.fetchShops();
  }, []);

  const handleOpenModal = useCallback((shop = null) => {
    setShopData(
      shop
        ? {
            name: shop.name || "",
            latitude: shop.latitude || "",
            longitude: shop.longitude || "",
            street: shop.street || "",
            city: shop.city || "",
            state: shop.state || "",
            postal_code: shop.postal_code || "",
            country: shop.country || "",
          }
        : {
            name: "",
            latitude: "",
            longitude: "",
            street: "",
            city: "",
            state: "",
            postal_code: "",
            country: "",
          }
    );
    setEditMode(!!shop);
    if (shop) setCurrentShopId(shop.id);
    setIsModalOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!shopData.name) {
      setSnackbar({
        open: true,
        message: "Please enter shop name",
        severity: "error",
      });
      return;
    }

    try {
      if (editMode) {
        await shopStore.updateShop(currentShopId, shopData);
        setSnackbar({
          open: true,
          message: "Shop updated successfully",
          severity: "success",
        });
      } else {
        await shopStore.createShop(shopData);
        setSnackbar({
          open: true,
          message: "Shop created successfully",
          severity: "success",
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error saving shop: " + error.message,
        severity: "error",
      });
    }
  }, [editMode, currentShopId, shopData]);

  const handleDelete = useCallback(async (shopId, shopName) => {
    if (window.confirm(`Are you sure you want to delete "${shopName}"?`)) {
      try {
        await shopStore.deleteShop(shopId);
        setSnackbar({
          open: true,
          message: "Shop deleted successfully",
          severity: "success",
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Error deleting shop: " + error.message,
          severity: "error",
        });
      }
    }
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setShopData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  // Rows memo
  const rows = useMemo(
    () =>
      shopStore.shops.map((shop) => ({
        id: shop.id,
        name: shop.name,
        location: `${shop.street}, ${shop.city}, ${shop.state} ${shop.postal_code}, ${shop.country}`,
        coordinates: `${shop.latitude}, ${shop.longitude}`,
        original: shop,
      })),
    [shopStore.shops]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Shop Name",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 2,
        minWidth: 300,
        renderCell: (params) => (
          <Box display="flex" alignItems="center">
            <LocationOnIcon sx={{ color: "#22C55E", mr: 1 }} fontSize="small" />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "coordinates",
        headerName: "Coordinates",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const shop = params.row.original;
          return (
            <Box>
              <IconButton
                onClick={() => handleOpenModal(shop)}
                sx={{
                  color: "#22C55E",
                  mr: 1,
                  "&:hover": { backgroundColor: "#BBF7D0" },
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(shop.id, shop.name)}
                sx={{
                  color: "#6B7280",
                  "&:hover": { backgroundColor: "#E5E7EB" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [handleDelete, handleOpenModal]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <StoreIcon fontSize="large" sx={{ mr: 2, color: "#22C55E" }} />
            <Typography variant="h4" fontWeight="bold">
              Shops Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your shop locations and details from this dashboard
          </Typography>
        </CardContent>
      </Card>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Button
          variant="contained"
          onClick={() => handleOpenModal()}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            boxShadow: 2,
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
        >
          Add New Shop
        </Button>

        {shopStore.isLoading && (
          <Box display="flex" alignItems="center">
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Loading shops...
            </Typography>
          </Box>
        )}
      </Box>

      {shopStore.error ? (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => shopStore.clearError()}
        >
          {shopStore.error}
        </Alert>
      ) : (
        <CustomDataGridPro
          rows={rows}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={shopStore.shops.length}
          isLoading={shopStore.isLoading}
        />
      )}

      {/* Modal for Adding/Editing Shop */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ elevation: 5, sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{ pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Box display="flex" alignItems="center">
            {editMode ? (
              <>
                <EditIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Edit Shop: {shopData.name}</Typography>
              </>
            ) : (
              <>
                <AddIcon sx={{ mr: 1, color: "#22C55E" }} />
                <Typography variant="h6">Add New Shop</Typography>
              </>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Shop Name"
                variant="outlined"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={shopData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StoreIcon fontSize="small" sx={{ color: "#22C55E" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                sx={{ mb: 1, display: "flex", alignItems: "center" }}
              >
                <LocationOnIcon sx={{ mr: 1, color: "#22C55E" }} />
                Location Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Street Address"
                fullWidth
                variant="outlined"
                value={shopData.street}
                onChange={(e) => handleInputChange("street", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                variant="outlined"
                value={shopData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="State/Province"
                fullWidth
                variant="outlined"
                value={shopData.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Postal Code"
                fullWidth
                variant="outlined"
                value={shopData.postal_code}
                onChange={(e) =>
                  handleInputChange("postal_code", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                fullWidth
                variant="outlined"
                value={shopData.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                sx={{ mb: 1, display: "flex", alignItems: "center" }}
              >
                <MapIcon sx={{ mr: 1, color: "#22C55E" }} />
                Coordinates
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Latitude"
                fullWidth
                variant="outlined"
                value={shopData.latitude}
                onChange={(e) => handleInputChange("latitude", e.target.value)}
                placeholder="e.g. 40.7128"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Longitude"
                fullWidth
                variant="outlined"
                value={shopData.longitude}
                onChange={(e) => handleInputChange("longitude", e.target.value)}
                placeholder="e.g. -74.0060"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Button
            onClick={() => setIsModalOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "uppercase",
              color: "#000",
              borderColor: "#000",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "#000",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={editMode ? <EditIcon /> : <AddIcon />}
            sx={{
              backgroundColor: "#22C55E",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#16A34A" },
            }}
          >
            {editMode ? "Update Shop" : "Add Shop"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ShopComponent;
